import type { Sources } from '../domain/Source'
import { Source } from '../domain/Source'
import type { Languages } from '@s4l/i18n/types/Languages'
import type { Config } from '../../_config'
import { FactoryQueryParams } from '../../_queryparams/FactoryQueryParams'
import type { ALLOWED_MARKETS } from '../domain/Market'
import { Market } from '../domain/Market'
import { Language } from '../domain/Language'
import { DateModel } from '../../_kernel/Date'
import { ListOfPax } from '../domain/ListOfPax'
import { PartialSearch } from '../domain/PartialSearch'
import { ForbiddenSharedRepository } from './ForbiddenSharedRepository'

export class QueryParamSharedRepository extends ForbiddenSharedRepository {
  static MARKET_QUERY_PARAMS = 'market'
  static QUERY_PARAM_LANGUAGE = 'lang'
  static QUERY_PARAM_SOURCE = 'source'

  static create({ config }: { config: Config }) {
    return new QueryParamSharedRepository(config)
  }

  constructor(config: Config) {
    super(config)
  }

  async market() {
    const params = await FactoryQueryParams.create({ config: this.config }).params()
    const [error, market] = params.getByQuery<(typeof ALLOWED_MARKETS)[number]>({
      query: QueryParamSharedRepository.MARKET_QUERY_PARAMS,
    })

    if (error) return Market.empty()

    return Market.createOrEmpty({
      value: market,
    })
  }

  async language() {
    const params = await FactoryQueryParams.create({ config: this.config }).params()
    const [error, language] = params.getByQuery<Languages>({
      query: QueryParamSharedRepository.QUERY_PARAM_LANGUAGE,
    })

    if (error) return Language.empty()

    return Language.createOrEmpty({
      value: language,
    })
  }

  async partialSearch() {
    const params = await FactoryQueryParams.create({ config: this.config }).params()
    const [errorCheckIn, checkIn] = params.getByQuery<string>({
      query: 'checkIn',
      options: {
        caseSensitive: false,
      },
    })
    const [errorCheckOut, checkOut] = params.getByQuery<string>({
      query: 'checkOut',
      options: {
        caseSensitive: false,
      },
    })
    const [errorAdults, adults] = params.getByQuery<string>({
      query: 'adults',
    })

    const [errorChildren, children] = params.getByQuery<string>({
      query: 'children',
      options: {
        required: false,
      },
    })

    if (errorCheckIn || errorCheckOut || errorAdults || errorChildren) {
      return PartialSearch.default()
    }

    return PartialSearch.create({
      checkIn: DateModel.create({ value: checkIn }),
      checkOut: DateModel.create({ value: checkOut }),
      paxes: ListOfPax.fromQueryParams({ adults, children }),
    })
  }

  async source(): Promise<Source> {
    const params = await FactoryQueryParams.create({ config: this.config }).params()
    const [error, source] = params.getByQuery<Sources>({
      query: QueryParamSharedRepository.QUERY_PARAM_SOURCE,
    })

    if (error) return Source.empty()

    return Source.createOrEmpty({
      value: source,
    })
  }
}
